import ArrowDownIcon from 'components/common/Icons/ArrowDownIcon';
import { DESKTOP_MIN_WIDTH } from 'constants';
import useLang from 'hooks/useLang';
import usePage from 'hooks/usePage';
import styled from 'styled-components';
import { paragraph500Mixin, paragraph700Mixin } from 'styles';
import DiscountApplyFailIcon from 'assets/ver2/icons/alert-circle-icon.svg';
import SizeBox from 'components/common/SizeBox';

const DiscountAvailableSection = ({ onClick, availability, failure }) => {
  const { t } = useLang();
  const { quantity = 0, isEmpty = false } = availability || {};
  const {
    isVisible: isFailureVisible = false,
    isApplying: isApplyingDiscount = false,
    canRetry = false,
    onRetry,
  } = failure || {};
  const { isAddTransactionPage } = usePage();

  if (isAddTransactionPage && isEmpty) {
    return (
      <FailureSection>
        <FailureContent
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FailureTitle>{t('label_offer')}</FailureTitle>
          <SizeBox height={4} />
          <FailureMessage>
            {t('label_discount_section_empty_content')}
          </FailureMessage>
        </FailureContent>
      </FailureSection>
    );
  }
  if (isFailureVisible) {
    return (
      <FailureSection>
        <FailureContent
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <FailureTitle>
            {t('label_offer')}
            <img src={DiscountApplyFailIcon} width={20} height={20} alt="" />
          </FailureTitle>
          <SizeBox height={4} />
          <FailureMessage>
            {t('label_discount_validate_fail_msg')}
          </FailureMessage>
          {canRetry && onRetry && (
            <FailureButton
              type="button"
              onClick={onRetry}
              disabled={isApplyingDiscount}
            >
              {!isApplyingDiscount && t('button_reload')}
            </FailureButton>
          )}
        </FailureContent>
      </FailureSection>
    );
  }
  // The add-transaction empty and failure states are handled above, so by here
  // isAddTransactionPage implies a non-empty offer list. The remaining renders
  // share one DiscountsAvailable row and differ only in the optional "Offer"
  // header (add-transaction) and the content copy.
  const availableContent = isAddTransactionPage
    ? t('button_view_offer')
    : isEmpty
      ? t('label_discount_section_empty_content')
      : `${t('form_placeholder_discount_select')} (${quantity} ${t(
          'discount_available_for_transaction',
        )})`;

  return (
    <>
      {isAddTransactionPage && (
        <>
          <FailureTitle>{t('label_offer')}</FailureTitle>
          <SizeBox height={4} />
        </>
      )}
      <Section>
        <DiscountsAvailable
          onClick={onClick}
          $isAddTransactionPage={isAddTransactionPage}
        >
          <Content $isAddTransactionPage={isAddTransactionPage}>
            {availableContent}
          </Content>
          <ArrowDownIcon
            color="var(--ds-c-blue-hyperlink-default)"
            size={{ width: 12, height: 10 }}
            styles={{
              transform: 'rotate(-90deg)',
            }}
          />
        </DiscountsAvailable>
      </Section>
    </>
  );
};

const Section = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 44px;
  margin-bottom: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
  }
`;
const DiscountsAvailable = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-inline: 10px;
  border-radius: 12px;
  border: 1px solid var(--ds-bg-2);
  height: 44px;
  width: 100%;
  cursor: pointer;

  ${(props) =>
    props.$isAddTransactionPage &&
    `
    background-color: #dfe0ff4d;
    border: none;
  `}
`;
const Content = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-blue-disabled);
  margin-bottom: 0px;
  text-align: left;
  text-transform: uppercase;
  width: 100%;

  ${(props) =>
    props.$isAddTransactionPage &&
    `
    color: #303c9a;
  `}
`;

const FailureSection = styled.div`
  width: 100%;
  min-height: 44px;
  padding: 0px;
  border-radius: 12px;
  border: none;
  background: var(--ds-c-white);
  text-align: start;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
  }
`;

const FailureTitle = styled.div`
  display: flex;
  ${paragraph700Mixin};
  gap: 2px;
`;
const FailureContent = styled.div`
  cursor: default;
`;

const FailureMessage = styled.p`
  ${paragraph500Mixin};
`;

const FailureButton = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  ${paragraph500Mixin};
  color: var(--ds-c-blue-hyperlink-default);
  text-decoration: underline;

  &:disabled {
    color: var(--ds-c-blue-disabled);
    cursor: not-allowed;
  }
`;

export default DiscountAvailableSection;
